import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronDown, ChevronRight, ExternalLink, Check, 
  Smartphone, Monitor, Volume2, Users,
  Gift, ArrowLeft, Sparkles, Zap, Trophy, Timer, Headphones, Rocket
} from 'lucide-react';
import { FaTwitch, FaTiktok, FaYoutube, FaInstagram } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GoLiveGuide - Full reference page for streaming setup
 * 
 * TWO PATHS:
 * 1. Quick Start (2 min) - TikTok Business + Live Mobile - FEATURED
 * 2. Pro Setup - Full 2-device audio setup
 * 
 * Accessible via /go-live-guide
 */

const TWO_DEVICE_DIAGRAM_URL = 'https://static.prod-images.emergentagent.com/jobs/cb79f670-214b-4957-af67-c4eb9237b946/images/0b770061057eac9964bc142488cc8df8149868048989b34dac59315d170eb0c0.png';

// Collapsible section component
const CollapsibleSection = ({ title, icon: Icon, iconColor, defaultOpen = false, badge, badgeColor = '#F2D857', children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-800 rounded-xl overflow-hidden bg-[#0a0a0a]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center gap-4 hover:bg-[#111] transition text-left"
      >
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: iconColor + '20' }}
        >
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-white text-lg">{title}</h3>
            {badge && (
              <span 
                className="px-2 py-0.5 rounded-full text-xs font-bold"
                style={{ backgroundColor: badgeColor, color: badgeColor === '#F2D857' ? 'black' : 'white' }}
              >
                {badge}
              </span>
            )}
          </div>
        </div>
        <ChevronDown 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      
      {isOpen && (
        <div className="p-4 pt-0 border-t border-gray-800 animate-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};

const GoLiveGuide = () => {
  const navigate = useNavigate();
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for active session on mount
  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        
        if (!token || !userStr) {
          setIsLoading(false);
          return;
        }

        const user = JSON.parse(userStr);
        if (!user?.id) {
          setIsLoading(false);
          return;
        }

        // Fetch user's active session
        const response = await fetch(`${API_URL}/api/sessions/reviewer/${user.id}/active`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data?.session_id) {
            setActiveSessionId(data.session_id);
          }
        }
      } catch (error) {
        console.error('Error checking active session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkActiveSession();
  }, []);

  // Navigate to session (active or create new)
  const handleStartSession = () => {
    if (activeSessionId) {
      navigate(`/session/${activeSessionId}`);
    } else {
      // Go to reviewer dashboard to create a session
      navigate('/reviewer-dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-b from-[#F2D857]/10 to-transparent">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link 
            to="/reviewer-dashboard" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="text-5xl animate-bounce">🎤</div>
            <div>
              <h1 className="text-4xl font-black text-white">
                Go Live <span className="text-[#F2D857]">Guide</span>
              </h1>
              <p className="text-gray-400 mt-1">Everything you need to start streaming music reviews</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-12 space-y-4">
        
        {/* ============================================ */}
        {/* QUICK START SECTION - FEATURED */}
        {/* ============================================ */}
        <div className="border-2 border-[#F2D857] rounded-xl overflow-hidden bg-gradient-to-br from-[#F2D857]/10 to-transparent">
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-[#F2D857] flex items-center justify-center">
                <Timer className="w-7 h-7 text-black" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-black text-white">Quick Start</h2>
                  <span className="px-3 py-1 rounded-full bg-[#F2D857] text-black text-sm font-bold">
                    ⚡ 2 MINUTES
                  </span>
                </div>
                <p className="text-gray-400">Go live TODAY with just your phone!</p>
              </div>
            </div>

            {/* What you need */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-3 bg-black/50 rounded-xl">
                <Smartphone className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-white font-medium">Smartphone</p>
                  <p className="text-gray-500 text-sm">iPhone or Android</p>
                </div>
                <Check className="w-5 h-5 text-green-400 ml-auto" />
              </div>
              <div className="flex items-center gap-3 p-3 bg-black/50 rounded-xl">
                <Headphones className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-white font-medium">Headphones with Mic</p>
                  <p className="text-gray-500 text-sm">Wired works best!</p>
                </div>
                <Check className="w-5 h-5 text-green-400 ml-auto" />
              </div>
            </div>

            {/* TikTok Business Setup */}
            <div className="bg-black/50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center border border-gray-700">
                  <FaTiktok className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Step 1: TikTok Business Account</h3>
                  <p className="text-green-400 text-sm">Free! No followers required! 🎉</p>
                </div>
              </div>
              <ol className="space-y-2 text-sm text-gray-300 ml-4">
                <li className="flex items-start gap-2">
                  <span className="text-[#F2D857] font-bold">1.</span>
                  Open TikTok → Profile → ☰ Menu → Settings
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#F2D857] font-bold">2.</span>
                  Account → Switch to Business Account
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#F2D857] font-bold">3.</span>
                  Select "Music" as your category
                </li>
              </ol>
            </div>

            {/* Go Live Steps */}
            <div className="bg-black/50 rounded-xl p-4 mb-6">
              <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#F2D857]" />
                Step 2: Go Live!
              </h3>
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-xs">1</div>
                  <p className="text-gray-300">Open your MCM session → <strong className="text-white">"Live Mobile"</strong></p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-xs">2</div>
                  <p className="text-gray-300"><strong className="text-white">Phone prep:</strong> 🔕 DND • 📱 Portrait Lock • ☀️ Screen On</p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-xs">3</div>
                  <p className="text-gray-300">TikTok → Go Live → <strong className="text-white">Mobile Gaming</strong> → <strong className="text-white">Screen Share</strong></p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F2D857] text-black font-bold flex items-center justify-center flex-shrink-0 text-xs">4</div>
                  <p className="text-gray-300">Switch back to <strong className="text-white">MCM tab</strong></p>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 text-white font-bold flex items-center justify-center flex-shrink-0 text-xs">✓</div>
                  <p className="text-green-400 font-medium">You're LIVE! 🎉 Review tracks & engage with chat!</p>
                </li>
              </ol>
            </div>

            {/* CTA */}
            <button
              onClick={handleStartSession}
              className="w-full py-4 rounded-full font-bold text-black text-lg transition-all hover:scale-[1.01] flex items-center justify-center gap-2"
              style={{ 
                background: 'linear-gradient(180deg, #F2D857 0%, #C9A227 100%)',
                boxShadow: '0 4px 20px rgba(242, 216, 87, 0.3)'
              }}
            >
              <Rocket className="w-5 h-5" />
              {activeSessionId ? 'Go to My Session!' : 'Start a Session Now!'}
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 py-4">
          <div className="flex-1 h-px bg-gray-800"></div>
          <span className="text-gray-500 text-sm">OR LEVEL UP WITH</span>
          <div className="flex-1 h-px bg-gray-800"></div>
        </div>

        {/* ============================================ */}
        {/* PRO SETUP SECTION */}
        {/* ============================================ */}
        <CollapsibleSection 
          title="Pro Setup: Full Audio Experience" 
          icon={Trophy}
          iconColor="#A855F7"
          badge="10-15 min"
          badgeColor="#A855F7"
        >
          <div className="space-y-4 mt-4">
            <p className="text-gray-400">
              Want your audience to hear crystal-clear music + your voice separately? This is the pro setup.
            </p>

            {/* Diagram */}
            <div className="rounded-xl overflow-hidden border border-purple-500/30">
              <img 
                src={TWO_DEVICE_DIAGRAM_URL} 
                alt="Two device setup diagram" 
                className="w-full h-auto"
              />
            </div>

            {/* Explanation */}
            <div className="bg-[#111] rounded-xl p-4 border border-gray-800">
              <h4 className="font-bold text-purple-400 mb-3">How it works:</h4>
              <div className="space-y-2 text-sm">
                <p className="text-gray-300">
                  <span className="text-purple-400 font-bold">Device 1:</span> Your streaming phone/laptop (goes LIVE)
                </p>
                <p className="text-gray-300">
                  <span className="text-purple-400 font-bold">Device 2:</span> Runs MCM and plays the music
                </p>
                <p className="text-gray-300">
                  <span className="text-purple-400 font-bold">Audio Interface:</span> Combines music + your mic via Bluetooth/USB
                </p>
              </div>
            </div>

            {/* Second Device Options */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <h4 className="font-bold text-blue-400 mb-2">Second device options:</h4>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>• Spare phone, tablet, or laptop</li>
                <li>• Family member's device (borrow it!)</li>
                <li>• Old phone on WiFi (doesn't need cell service)</li>
              </ul>
            </div>

            {/* Equipment */}
            <div className="bg-[#111] rounded-xl p-4 border border-[#F2D857]/30">
              <h4 className="font-bold text-[#F2D857] mb-3">Key Equipment:</h4>
              <a 
                href="https://amzn.to/43sFuiF" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-black/50 rounded-lg hover:bg-black/70 transition"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🥇</span>
                  <div>
                    <p className="text-white font-medium">Maono Podcast Bundle</p>
                    <p className="text-gray-500 text-sm">Audio interface + mic combo</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[#F2D857] font-bold">~$60</p>
                  <ExternalLink className="w-4 h-4 text-gray-500 ml-auto" />
                </div>
              </a>
              <p className="text-gray-500 text-xs mt-2 text-center">
                💡 Or find similar on TikTok Shop for less!
              </p>
            </div>
          </div>
        </CollapsibleSection>

        {/* Platform Selection for Pro */}
        <CollapsibleSection 
          title="Platform Guide" 
          icon={Users}
          iconColor="#F2D857"
        >
          <div className="space-y-4 mt-4">
            {/* Twitch */}
            <div className="p-4 bg-[#111] rounded-xl border border-gray-800">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#9146FF] flex items-center justify-center">
                  <FaTwitch className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-white text-lg">Twitch</h4>
                    <span className="px-2 py-0.5 rounded-full bg-[#F2D857] text-black text-xs font-bold">
                      ⭐ RECOMMENDED
                    </span>
                  </div>
                  <p className="text-green-400 text-sm">No follower requirement!</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a 
                  href="https://www.twitch.tv/signup" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#9146FF] rounded-lg text-white text-sm font-medium hover:bg-[#9146FF]/80 transition"
                >
                  Create Account <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href="https://dashboard.twitch.tv" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#9146FF] rounded-lg text-[#9146FF] text-sm font-medium hover:bg-[#9146FF]/10 transition"
                >
                  Dashboard <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* YouTube */}
            <div className="p-4 bg-[#111] rounded-xl border border-gray-800">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#FF0000] flex items-center justify-center">
                  <FaYoutube className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-white text-lg">YouTube</h4>
                  <p className="text-gray-400 text-sm">24hr verification first time only</p>
                </div>
              </div>
              <a 
                href="https://studio.youtube.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF0000] rounded-lg text-white text-sm font-medium hover:bg-[#FF0000]/80 transition"
              >
                YouTube Studio <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {/* Instagram */}
            <div className="p-4 bg-[#111] rounded-xl border border-gray-800">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF)' }}>
                  <FaInstagram className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-white text-lg">Instagram</h4>
                  <p className="text-gray-400 text-sm">1,000 followers + Professional account</p>
                </div>
              </div>
            </div>

            {/* Multi-platform */}
            <div className="p-4 bg-[#111] rounded-xl border border-[#F2D857]/30">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#F2D857] flex items-center justify-center">
                  <Users className="w-6 h-6 text-black" />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-white text-lg">Multiple Platforms</h4>
                  <p className="text-[#F2D857] text-sm">Go live everywhere at once!</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a 
                  href="https://restream.io" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F2D857] rounded-lg text-black text-sm font-medium hover:bg-[#F2D857]/80 transition"
                >
                  Restream <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href="https://streamlabs.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#F2D857] rounded-lg text-[#F2D857] text-sm font-medium hover:bg-[#F2D857]/10 transition"
                >
                  Streamlabs <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Equipment Guide */}
        <CollapsibleSection 
          title="Equipment Guide" 
          icon={Volume2}
          iconColor="#22C55E"
        >
          <div className="space-y-4 mt-4">
            <p className="text-gray-400 text-sm mb-4">
              Your personality is the star - gear is just a bonus! Start with what you have. ⭐
            </p>

            {/* Bronze */}
            <div className="p-4 bg-[#111] rounded-xl border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🥉</span>
                  <h4 className="font-bold text-white">Bronze Setup</h4>
                </div>
                <span className="text-green-400 font-bold">FREE</span>
              </div>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• Phone camera + earbuds with mic</li>
                <li>• Good lighting (face a window)</li>
                <li>• Quiet room</li>
              </ul>
              <p className="text-green-400 text-sm mt-2 font-medium">✓ You're ready to start!</p>
            </div>

            {/* Silver */}
            <div className="p-4 bg-[#111] rounded-xl border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🥈</span>
                  <h4 className="font-bold text-white">Silver Setup</h4>
                </div>
                <span className="text-gray-300 font-bold">$50-100</span>
              </div>
              <p className="text-gray-400 text-sm mb-2">Look 10x more professional!</p>
              <div className="flex flex-wrap gap-2">
                <a 
                  href="https://amzn.to/456SKdU" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#232F3E] rounded-lg text-white text-sm hover:bg-[#232F3E]/80 transition"
                >
                  Phone Tripod - $25 <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href="https://amzn.to/45Ch0op" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#232F3E] rounded-lg text-white text-sm hover:bg-[#232F3E]/80 transition"
                >
                  Ring Light - $30 <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Gold */}
            <div className="p-4 bg-[#111] rounded-xl border border-[#F2D857]/50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🥇</span>
                  <h4 className="font-bold text-white">Gold Setup</h4>
                </div>
                <span className="text-[#F2D857] font-bold">~$60</span>
              </div>
              <p className="text-gray-400 text-sm mb-2">
                The audio interface lets your audience hear the music!
              </p>
              <a 
                href="https://amzn.to/43sFuiF" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F2D857] rounded-lg text-black text-sm font-medium hover:bg-[#F2D857]/80 transition"
              >
                Maono Podcast Bundle <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {/* Diamond */}
            <div className="p-4 bg-[#111] rounded-xl border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💎</span>
                  <h4 className="font-bold text-white">Diamond Setup</h4>
                </div>
                <span className="text-gray-300 font-bold">$300+</span>
              </div>
              <p className="text-gray-400 text-sm mb-2">Full boss mode!</p>
              <div className="flex flex-wrap gap-2">
                <a 
                  href="https://amzn.to/4dGqzom" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#232F3E] rounded-lg text-white text-sm hover:bg-[#232F3E]/80 transition"
                >
                  Shure SM7B <ExternalLink className="w-3 h-3" />
                </a>
                <a 
                  href="https://amzn.to/3T5gpUM" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#232F3E] rounded-lg text-white text-sm hover:bg-[#232F3E]/80 transition"
                >
                  Focusrite Scarlett <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#F2D857]/20 to-[#F2D857]/5 rounded-xl p-6 border border-[#F2D857]/30 text-center">
          <Sparkles className="w-10 h-10 text-[#F2D857] mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Ready to Go Live?</h3>
          <p className="text-gray-400 mb-4">You've got everything you need. Now go make some noise!</p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleStartSession}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#F2D857] rounded-full text-black font-bold hover:bg-[#F2D857]/90 transition"
            >
              <Zap className="w-5 h-5" />
              {activeSessionId ? 'Go to My Session' : 'Start a Session'}
            </button>
            <a
              href="https://miccheckmillionaire.gumroad.com/l/zzlxg"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#F2D857] rounded-full text-[#F2D857] font-bold hover:bg-[#F2D857]/10 transition"
            >
              <Gift className="w-5 h-5" />
              Get the Full Blueprint
            </a>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-gray-500 text-sm">
          Questions? Reach out to support. We're here to help you succeed! 🎤💰
        </p>
      </div>
    </div>
  );
};

export default GoLiveGuide;
